import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import type { StorefrontPaymentConfig } from '../../services/storefrontService';

const paypalGlobal = (): any => (window as any).paypal;

const SCRIPT_ID = 'oa-storefront-paypal-sdk';

/** Lädt das PayPal-JS-SDK mit Partner-/Seller-Konfiguration (Plan 3.5) genau einmal je Konfiguration. */
export function loadPayPalSdk(config: StorefrontPaymentConfig): Promise<void> {
    const params = new URLSearchParams({ 'client-id': config.clientId, currency: config.currency, intent: 'capture', components: 'buttons' });
    if (config.sellerMerchantId) params.set('merchant-id', config.sellerMerchantId);
    const src = `https://www.paypal.com/sdk/js?${params.toString()}`;

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing && existing.src === src && paypalGlobal()) return Promise.resolve();
    existing?.remove();

    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = src;
        if (config.partnerAttributionId) script.setAttribute('data-partner-attribution-id', config.partnerAttributionId);
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('PayPal SDK konnte nicht geladen werden'));
        document.head.appendChild(script);
    });
}

interface Props {
    config: StorefrontPaymentConfig;
    /** PayPal-Order-ID aus dem Checkout (serverseitig erzeugt). */
    paypalOrderId: string;
    onApprove: (paypalOrderId: string) => Promise<void> | void;
    onError: () => void;
    loadingLabel: string;
    unavailableLabel: string;
}

const PayPalButtons: React.FC<Props> = ({ config, paypalOrderId, onApprove, onError, loadingLabel, unavailableLabel }) => {
    const container = useRef<HTMLDivElement>(null);
    const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

    useEffect(() => {
        let cancelled = false;
        loadPayPalSdk(config)
            .then(() => {
                if (cancelled || !container.current || !paypalGlobal()?.Buttons) {
                    if (!cancelled) setState('error');
                    return;
                }
                container.current.innerHTML = '';
                paypalGlobal()
                    .Buttons({
                        createOrder: () => Promise.resolve(paypalOrderId),
                        onApprove: (data: { orderID: string }) => onApprove(data.orderID),
                        onError: () => onError(),
                    })
                    .render(container.current);
                setState('ready');
            })
            .catch(() => !cancelled && setState('error'));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.clientId, config.sellerMerchantId, paypalOrderId]);

    return (
        <Box>
            {state === 'loading' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} role="status"><CircularProgress size={20} /> {loadingLabel}</Box>
            ) : null}
            {state === 'error' ? <Alert severity="warning">{unavailableLabel}</Alert> : null}
            <div ref={container} data-testid="paypal-buttons" />
        </Box>
    );
};

export default PayPalButtons;
